import { useState, useEffect } from 'react';
import { bitable, FieldType } from '@lark-base-open/js-sdk';
/**
 * 自定义 Hook - 获取多维表格数据
 */
export function useBaseData() {
    const [appToken, setAppToken] = useState('');
    const [tableId, setTableId] = useState('');
    const [fields, setFields] = useState([]);
    const [attachmentFields, setAttachmentFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        initializeData();
    }, []);
    const initializeData = async () => {
        try {
            setLoading(true);
            setError(null);
            // 获取当前表格
            const table = await bitable.base.getActiveTable();
            const currentTableId = table.id;
            setTableId(currentTableId);
            // 获取 App Token (从URL中获取)
            const selection = await bitable.base.getSelection();
            if (selection?.baseId) {
                setAppToken(selection.baseId);
            }
            // 获取所有字段
            const fieldMetaList = await table.getFieldMetaList();
            setFields(fieldMetaList);
            // 筛选出附件字段
            const attachmentFieldList = fieldMetaList.filter(field => field.type === FieldType.Attachment);
            setAttachmentFields(attachmentFieldList);
        }
        catch (err) {
            console.error('Failed to initialize data:', err);
            setError(err.message || '初始化失败');
        }
        finally {
            setLoading(false);
        }
    };
    /**
     * 获取附件列表
     */
    const getAttachments = async (recordId, fieldId) => {
        try {
            const table = await bitable.base.getActiveTable();
            const field = await table.getField(fieldId);
            const attachments = await field.getValue(recordId);
            if (!attachments || !Array.isArray(attachments)) {
                return [];
            }
            return attachments.map(att => ({
                fileToken: att.token,
                name: att.name,
                type: att.type || 'unknown',
                size: att.size || 0,
                url: att.url
            }));
        }
        catch (error) {
            console.error('Failed to get attachments:', error);
            return [];
        }
    };
    /**
     * 获取字段值
     */
    const getFieldValue = async (recordId, fieldId) => {
        try {
            const table = await bitable.base.getActiveTable();
            const field = await table.getField(fieldId);
            const value = await field.getValue(recordId);
            if (value === null || value === undefined) {
                return '';
            }
            // 处理不同类型的值
            if (typeof value === 'string' || typeof value === 'number') {
                return String(value);
            }
            else if (Array.isArray(value)) {
                // 多选字段
                return value.map(v => v.text || v).join(',');
            }
            else if (typeof value === 'object' && value.text) {
                // 单选字段
                return value.text;
            }
            return String(value);
        }
        catch (error) {
            console.error('Failed to get field value:', error);
            return '';
        }
    };
    /**
     * 更新字段值
     */
    const updateFieldValue = async (recordId, fieldId, value) => {
        try {
            const table = await bitable.base.getActiveTable();
            const field = await table.getField(fieldId);
            const fieldMeta = await field.getMeta();
            console.log(`Updating field ${fieldId} (Type: ${fieldMeta.type}) for record ${recordId} with value: ${value}`);
            if (fieldMeta.type === FieldType.Url) {
                // 如果是超链接字段，需要写入对象格式
                // 注意：飞书多维表格的超链接字段写入格式可能需要 { text, link }
                await table.setCellValue(fieldId, recordId, {
                    text: 'Google Drive Link',
                    link: value
                });
            }
            else if (fieldMeta.type === FieldType.Text) {
                // 文本字段直接写入字符串
                await table.setCellValue(fieldId, recordId, value);
            }
            else {
                // 其他类型尝试直接写入
                console.warn(`Unknown field type ${fieldMeta.type}, trying to write as string/value`);
                await table.setCellValue(fieldId, recordId, value);
            }
            return true;
        }
        catch (error) {
            console.error('Failed to update field value:', error);
            return false;
        }
    };
    /**
     * 获取当前视图下的所有记录
     */
    const getAllRecords = async () => {
        try {
            const table = await bitable.base.getActiveTable();
            const view = await table.getActiveView();
            const recordList = await view.getVisibleRecordIdList();
            return recordList.filter((id) => !!id);
        }
        catch (error) {
            console.error('Failed to get all records:', error);
            return [];
        }
    };
    return {
        appToken,
        tableId,
        fields,
        attachmentFields,
        loading,
        error,
        getAttachments,
        getFieldValue,
        updateFieldValue,
        getAllRecords
    };
}
